import { KEEP_PLACE } from "@/lib/search";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { groupsShown } from "@/components/app/phone/group-words";
import { useTextGroups } from "@/components/app/phone/use-text-groups";
import { toast } from "sonner";
import { phoneKey, errorWords } from "@alm/domain";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/app/shell/page-header";
import { WorkspaceMark } from "@/components/app/workspace/workspace-mark";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useDockHidden } from "@/lib/workspace/dock-hidden";
import { useFacility } from "@/lib/facility/facility";
import { MessageSquare, Plus } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan, useCanAnywhere, useFeature } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { FileNumberDialog } from "@/routes/_authenticated/_front-desk/sarah/-components/file-number-dialog";
import { SmsComposer } from "@/routes/_authenticated/_front-desk/sarah/-components/sms-composer";
import { SmsEmpty } from "@/routes/_authenticated/_front-desk/sarah/-components/sms-empty";
import { SmsInbox } from "@/routes/_authenticated/_front-desk/sarah/-components/sms-inbox";
import {
  smsListSearch,
  smsListView,
  type SmsListSearch,
} from "@/routes/_authenticated/_front-desk/sarah/-components/sms-list-search";
import {
  fileNumberProps,
  smsInboxActionProps,
  type FilingTarget,
} from "@/routes/_authenticated/_front-desk/sarah/-components/sms-inbox-actions";
import {
  markAllReadWrites,
  needsReply,
  type SmsThread,
} from "@/routes/_authenticated/_front-desk/sarah/-components/sms-threads";
import { LineSwitch, type InboxLine } from "./-components/line-switch";
import { MyNumberPane } from "./-components/my-number-pane";
import { useDeskInbox } from "./-components/use-desk-inbox";

/* Typed by hand, ?with=9035309696 reaches the route as a number, and the router keeps any key the
   validator leaves out raw: so every key is returned, a number as text, anything else undefined. */
const searchText = (value: unknown) =>
  typeof value === "number"
    ? String(value)
    : typeof value === "string" && value
      ? value
      : undefined;

type TextsSearch = SmsListSearch & { t?: string; with?: string };

export const Route = createFileRoute("/_authenticated/ws/sarah/texts")({
  /* `?with=` is the link in a text forwarded to somebody's cell: it opens that
     conversation on their own number. */
  validateSearch: (search: Record<string, unknown>): TextsSearch => ({
    ...smsListSearch(search),
    t: searchText(search.t),
    with: searchText(search.with),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-messages"),
  component: SarahTexts,
});

const failed = (error: Error) => toast.error(errorWords(error));

/** The inbox is Core's, so a thread reads and replies the same in both places. The head counts
 * conversations owed a reply, not merely unread: that is what the desk is measured on. */
function SarahTexts() {
  const params = Route.useSearch();
  const { t, with: openWith } = params;
  const navigate = useNavigate({ from: Route.fullPath });
  const refine = (next: SmsListSearch) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const { text: term, change: setTerm } = useSearchParamText(params.q, (q) => refine({ q }));
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const canCompose = useCan("frontdesk:manage");
  const [composing, setComposing] = useState(false);
  const { threads, messages, agents, facilities, nameByNumber } = useDeskInbox(facilityId);
  /* A number nothing in the Directory names, on a thread that is not a
     candidate's: offered for filing, as Core's Texts does, so the next text,
     fax or email finds a name. */
  const canFileNumbers = useCan("directory:manage");
  const [filing, setFiling] = useState<FilingTarget | null>(null);
  /* Somebody holding a company number of their own reads its texts here too,
     beside the community's: one place for texts, as on their phone. */
  const calls = useFeature("sarah-calls");
  const canDial = useCanAnywhere("frontdesk:call");
  const myLine = useQuery({
    ...orpc.frontDesk.phone.myLine.queryOptions({ input: { limit: 50 } }),
    enabled: calls && canDial,
    staleTime: 15_000,
  });
  const own = myLine.data?.lines.find((held) => held.texting);
  /* Counted over every conversation: the newest fifty calls and texts held a day of a busy
     number's texts (9 Oct). */
  const myTexts = useQuery({
    ...orpc.frontDesk.phone.lineConversations.queryOptions({ input: { limit: 1 } }),
    enabled: Boolean(own),
    staleTime: 15_000,
  });
  const groups = useTextGroups(Boolean(own));
  const mineWaiting =
    (myTexts.data?.waiting ?? 0) + groupsShown(groups.data ?? [], { show: "waiting" }).length;
  const [line, setLine] = useState<InboxLine>(openWith ? "mine" : "desk");
  const mine = Boolean(own) && line === "mine";
  const dockHidden = useDockHidden();
  const unfiled = (thread: SmsThread) =>
    canFileNumbers && !thread.applicantId && !nameByNumber.has(phoneKey(thread.key));

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.frontDesk.messages.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.communications.sms.key() }),
    ]);

  const save = useMutation(
    orpc.frontDesk.messages.saveThreadStates.mutationOptions({ onError: failed }),
  );
  const setHandled = useMutation(
    orpc.frontDesk.messages.setThreadHandled.mutationOptions({
      onSuccess: async (_result, input) => {
        toast.success(input.handled ? "Marked handled" : "Reopened");
        await refresh();
      },
      onError: failed,
    }),
  );
  const agentAction = useMutation(
    orpc.frontDesk.messages.setThreadAgent.mutationOptions({
      onSuccess: async (_result, input) => {
        toast.success(
          input.action === "take_over" ? "This conversation is yours" : "Handed back to Sarah",
        );
        await refresh();
      },
      onError: failed,
    }),
  );

  const write = (thread: SmsThread, patch: { markRead?: true; starred?: boolean }) =>
    save.mutate(
      { states: [{ facilityId: thread.facilityId, threadKey: thread.key, ...patch }] },
      { onSuccess: () => void refresh() },
    );

  const markAll = async () => {
    for (const batch of markAllReadWrites(threads)) {
      const sent = await save.mutateAsync({ states: batch }).then(
        () => true,
        () => false,
      );
      if (!sent) break;
    }
    await refresh();
  };

  const owed = threads.filter(needsReply).length;
  /* Conversations, not messages: the Unread chip below counts conversations,
     and the two numbers side by side disagreed. */
  const unread = threads.filter((thread) => thread.unread > 0).length;
  const composeFacilities = (facilities.data ?? []).map((facility) => ({
    id: facility.id,
    name: facility.name,
  }));

  const head = (
    <div className="space-y-4">
      <PageHeader
        mark={<WorkspaceMark id="sarah" size={44} />}
        kicker="Sarah, Texts"
        title={
          mine
            ? "Texts on your number"
            : messages.isPending
              ? "Texts"
              : owed === 0
                ? "Nobody is waiting on a reply"
                : `${String(owed)} waiting on a reply`
        }
        meta={
          mine || messages.isPending
            ? undefined
            : `${String(threads.length)} conversations${unread > 0 ? `, ${String(unread)} unread` : ""}`
        }
        actions={
          !mine && (unread > 0 || canCompose) ? (
            <>
              {unread > 0 ? (
                <Button variant="outline" disabled={save.isPending} onClick={() => void markAll()}>
                  Mark all read
                </Button>
              ) : null}
              {canCompose ? (
                <Button onClick={() => setComposing(true)}>
                  <Plus /> New text
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />
      {own ? (
        <LineSwitch
          value={line}
          number={own.phoneNumber}
          onChange={setLine}
          deskWaiting={messages.isPending ? undefined : owed}
          mineWaiting={mineWaiting}
        />
      ) : null}
    </div>
  );
  /* With the inbox up, the heading goes inside it: on a phone it steps aside
     while a conversation is open. */
  const inbox = !mine && !messages.isPending && !messages.isError && threads.length > 0;
  /* A conversation on your number that holds a phone's screen puts the dock away: the heading
     and the switch step aside with it. */
  const reading = mine && dockHidden;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5">
      {inbox || reading ? null : head}

      {mine ? (
        <MyNumberPane facilityId={facilityId} texts openWith={openWith} />
      ) : messages.isPending ? (
        <Skeleton className="h-[32rem] w-full rounded-xl" />
      ) : messages.isError ? (
        <ReadFailed
          message="Couldn't load the texts."
          error={messages.error}
          onRetry={() => void messages.refetch()}
          retrying={messages.isRefetching}
          className="py-10"
        />
      ) : threads.length === 0 ? (
        <SmsEmpty
          icon={<MessageSquare />}
          title="No conversations yet"
          description="Texts to and from the community's line appear here."
        />
      ) : (
        <SmsInbox
          head={head}
          threads={threads}
          onOpenThread={(thread) => write(thread, { markRead: true })}
          onToggleStar={(thread) => write(thread, { starred: !thread.starred })}
          agents={agents.data ?? []}
          initialThreadId={t ?? null}
          list={smsListView(params, term, setTerm, refine)}
          {...smsInboxActionProps(agentAction, setHandled)}
          {...fileNumberProps(canFileNumbers, setFiling, unfiled)}
        />
      )}

      <FileNumberDialog filing={filing} onClose={() => setFiling(null)} />

      {composing ? (
        <SmsComposer
          facilities={
            composeFacilities.length > 0
              ? composeFacilities
              : facilityId === "all"
                ? []
                : [{ id: facilityId, name: "This community" }]
          }
          defaultFacilityId={facilityId}
          threads={threads}
          onClose={() => setComposing(false)}
          onSent={() => void refresh()}
        />
      ) : null}
    </div>
  );
}
